import type { CollectableFeeRow } from '@klear/collection'
import { useEffect, useMemo, useCallback } from 'react'
import { MoneyInput } from '@klear/ui/components/money-input'
import { Switch } from '@klear/ui/components/switch'
import TableAgGridBulkSelection from '@klear/ui/components/table-ag-grid-bulk-selection'
import { Loader2 } from 'lucide-react'
import type { ColDef, ICellRendererParams, RowClassParams, ValueGetterParams } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import {
  useCollectionOutletContext,
  type CollectableRecovery,
  type CollectionStepCtx
} from '@/app/collections/outlet-context'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import { COLLECTION_TYPE, type CollectionType } from '@/app/collections/steps'
import {
  COLLECTION_REFERENCE_HIGHLIGHT_ROW_CLASS,
  feeRowMatchesReferenceId,
  recoveryRowMatchesReferenceId
} from '@/app/collections/collection-highlight'
import { Money } from '@klear/money'
import * as changeCase from 'change-case'
import noop from 'lodash/noop'

const DESCRIPTION_BY_COLLECTION_TYPE: Record<CollectionType, string> = {
  [COLLECTION_TYPE.FEES]: 'Select the collectable fees to include in this collection.',
  [COLLECTION_TYPE.ADVANCE_RECOVERY]: 'Select the advance recoveries to include in this collection.'
}

function FeeAmountCellRenderer(props: ICellRendererParams<CollectableFeeRow>) {
  const { state, setState } = useCollectionOutletContext()
  const row = props.data
  if (row == null || row.fee.id == null) return null
  const feeId = row.fee.id

  const selected = state.selectedFees.find((f) => f.feeId === feeId)
  const remaining = row.remainingAmount
  const money = selected?.money ?? remaining
  const disabled = !selected
  const exceedsRemaining = money.greaterThan(remaining)

  const onChange = (next: Money) => {
    setState((s) => ({
      ...s,
      selectedFees: s.selectedFees.map((f) => (f.feeId === feeId ? { ...f, money: next } : f))
    }))
  }

  return (
    <div className="flex items-center h-full py-1">
      <MoneyInput
        label=""
        value={money}
        onChange={onChange}
        currencyDisabled
        max={remaining}
        disabled={disabled}
        className={exceedsRemaining ? 'border border-destructive rounded' : undefined}
      />
    </div>
  )
}

function FeeInvoiceOfferCellRenderer(props: ICellRendererParams<CollectableFeeRow>) {
  const row = props.data
  if (!row) return null
  const offerId = row.invoiceOffer?.id ?? row.fee.parentId
  if (offerId == null || offerId <= 0) return '-'
  return row.invoiceOffer?.idDisplay ?? `#${offerId}`
}

function RecoveryOfferCellRenderer(props: ICellRendererParams<CollectableRecovery>) {
  const row = props.data
  if (!row?.id) return '-'
  return row.idDisplay || `#${row.id}`
}

function FeeItemsList() {
  const { state, setState, collectableFeeRows, invoiceOfferDisplayMap, isCollectableLoading, highlightReferenceId } =
    useCollectionOutletContext()
  const agGrid = useKlearAgGrid()
  const defaultColDef = useMemo(() => ({ ...agGrid.defaultColDef, flex: 1 }), [agGrid.defaultColDef])
  const collectableItems = useMemo(
    () => collectableFeeRows.filter((r) => r.remainingAmount.isPositive()),
    [collectableFeeRows]
  )
  const selectedSet = useMemo(() => new Set(state.selectedFees.map((f) => f.feeId)), [state.selectedFees])

  const getRowClass = useCallback(
    (params: RowClassParams<CollectableFeeRow>) => {
      const row = params.data
      if (row == null || highlightReferenceId == null) return undefined
      return feeRowMatchesReferenceId(row, highlightReferenceId) ? COLLECTION_REFERENCE_HIGHLIGHT_ROW_CLASS : undefined
    },
    [highlightReferenceId]
  )

  useEffect(() => {
    if (collectableItems.length > 0) {
      setState((s) => {
        if (s.selectedFees.length > 0) return s
        return {
          ...s,
          selectedFees: collectableItems
            .filter((row) => row.fee.id != null)
            .map((row) => ({ feeId: row.fee.id!, money: row.remainingAmount }))
        }
      })
    }
  }, [collectableItems, setState])

  const handleSelected = useCallback(
    (row: CollectableFeeRow, isChecked: boolean) => {
      const id = row.fee.id
      if (id == null) return
      setState((s) => {
        const exists = s.selectedFees.some((f) => f.feeId === id)
        if (isChecked && !exists) {
          return {
            ...s,
            selectedFees: [...s.selectedFees, { feeId: id, money: row.remainingAmount }]
          }
        }
        if (!isChecked && exists) {
          return { ...s, selectedFees: s.selectedFees.filter((f) => f.feeId !== id) }
        }
        return s
      })
    },
    [setState]
  )

  const isSelected = useCallback(
    (row: CollectableFeeRow) => (row.fee.id != null ? selectedSet.has(row.fee.id) : false),
    [selectedSet]
  )

  const columnDefs = useMemo<ColDef<CollectableFeeRow>[]>(
    () => [
      {
        headerName: 'Type',
        colId: 'type',
        valueGetter: (p: ValueGetterParams<CollectableFeeRow>) => changeCase.capitalCase(p.data?.fee.type ?? 'fee'),
        filter: 'agTextColumnFilter',
        flex: 1
      },
      {
        headerName: 'Invoice offer',
        colId: 'invoiceOffer',
        valueGetter: (p: ValueGetterParams<CollectableFeeRow>) => {
          const parentId = p.data?.fee.parentId
          if (parentId == null) return ''
          return invoiceOfferDisplayMap.get(parentId) ?? `#${parentId}`
        },
        cellRenderer: FeeInvoiceOfferCellRenderer,
        filter: 'agTextColumnFilter',
        flex: 1
      },
      {
        headerName: 'Fee total',
        colId: 'feeTotal',
        field: 'fee.amount',
        type: 'rightAligned',
        flex: 1
      },
      {
        headerName: 'Remaining',
        colId: 'remainingAmount',
        field: 'remainingAmount',
        type: 'rightAligned',
        flex: 1
      },
      {
        headerName: 'Amount to collect',
        colId: 'amountToCollect',
        cellRenderer: FeeAmountCellRenderer,
        sortable: false,
        filter: false,
        flex: 2,
        minWidth: 260
      }
    ],
    [invoiceOfferDisplayMap]
  )

  const totalToCollect = Money.sum(state.selectedFees.map((f) => f.money))

  if (isCollectableLoading) return <p className="text-sm text-muted-foreground">Loading collectable fees…</p>
  if (collectableFeeRows.length === 0)
    return <p className="text-sm text-muted-foreground">No collectable fees for this trader.</p>

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <span className="text-sm text-muted-foreground">
          {state.selectedFees.length} of {collectableItems.length} selected
        </span>
      </div>
      <TableAgGridBulkSelection<CollectableFeeRow>
        isBulkUpdating={true}
        rowData={collectableItems}
        columnDefs={columnDefs}
        bulkItemSelected={isSelected}
        onBulkItemSelected={handleSelected}
        disableBulkSelectionItem={(r) => !r.remainingAmount.isPositive()}
        rowHeight={56}
        headerHeight={40}
        animateRows={true}
        enableCellTextSelection={false}
        pagination={false}
        paginationPageSize={20}
        theme={agGrid.theme}
        onGridReady={noop}
        quickFilterText=""
        defaultColDef={defaultColDef}
        columnTypes={agGrid.columnTypes}
        dataTypeDefinitions={agGrid.dataTypeDefinitions}
        className="w-full h-[360px]"
        getRowClass={getRowClass}
      />
      <div className="flex justify-between text-sm font-medium border-t pt-2">
        <span>Total to collect</span>
        <span>{totalToCollect.toString()}</span>
      </div>
    </div>
  )
}

function AmountCellRenderer(props: ICellRendererParams<CollectableRecovery>) {
  const { state, setState } = useCollectionOutletContext()
  const recovery = props.data
  if (!recovery) return null

  const selected = state.selectedRecoveries.find((r) => r.orderEventId === recovery.id)
  const remaining = recovery.remainingAmount
  const money = selected?.money ?? remaining
  const disabled = !selected
  const exceedsRemaining = money.greaterThan(remaining)

  const onChange = (next: Money) => {
    setState((s) => ({
      ...s,
      selectedRecoveries: s.selectedRecoveries.map((r) => (r.orderEventId === recovery.id ? { ...r, money: next } : r))
    }))
  }

  return (
    <div className="flex items-center h-full py-1">
      <MoneyInput
        label=""
        value={money}
        onChange={onChange}
        currencyDisabled
        max={remaining}
        disabled={disabled}
        className={exceedsRemaining ? 'border border-destructive rounded' : undefined}
      />
    </div>
  )
}

function RecoveryItemsList() {
  const {
    state,
    setState,
    collectableRecoveries,
    isCollectableLoading,
    includeNotDueRecoveries,
    setIncludeNotDueRecoveries,
    highlightReferenceId
  } = useCollectionOutletContext()
  const agGrid = useKlearAgGrid()
  const defaultColDef = useMemo(() => ({ ...agGrid.defaultColDef, flex: 1 }), [agGrid.defaultColDef])

  const getRowClass = useCallback(
    (params: RowClassParams<CollectableRecovery>) => {
      const row = params.data
      if (row == null || highlightReferenceId == null) return undefined
      return recoveryRowMatchesReferenceId(row, highlightReferenceId)
        ? COLLECTION_REFERENCE_HIGHLIGHT_ROW_CLASS
        : undefined
    },
    [highlightReferenceId]
  )

  const collectableItems = useMemo(
    () => collectableRecoveries.filter((r) => r.remainingAmount.isPositive()),
    [collectableRecoveries]
  )

  useEffect(() => {
    if (collectableItems.length > 0) {
      setState((s) => {
        if (s.selectedRecoveries.length > 0) return s
        return {
          ...s,
          selectedRecoveries: collectableItems.map((r) => ({
            orderEventId: r.id,
            money: r.remainingAmount,
            endDate: r.endDate
          }))
        }
      })
    }
  }, [collectableItems, setState])

  const isSelected = useCallback(
    (recovery: CollectableRecovery) => state.selectedRecoveries.some((r) => r.orderEventId === recovery.id),
    [state.selectedRecoveries]
  )

  const handleSelected = useCallback(
    (recovery: CollectableRecovery, isChecked: boolean) => {
      setState((s) => {
        const exists = s.selectedRecoveries.some((r) => r.orderEventId === recovery.id)
        if (isChecked && !exists) {
          return {
            ...s,
            selectedRecoveries: [
              ...s.selectedRecoveries,
              {
                orderEventId: recovery.id,
                money: recovery.remainingAmount,
                endDate: recovery.endDate
              }
            ]
          }
        }
        if (!isChecked && exists) {
          return {
            ...s,
            selectedRecoveries: s.selectedRecoveries.filter((r) => r.orderEventId !== recovery.id)
          }
        }
        return s
      })
    },
    [setState]
  )

  const columnDefs = useMemo<ColDef<CollectableRecovery>[]>(
    () => [
      {
        headerName: 'Offer',
        colId: 'idDisplay',
        valueGetter: (p: ValueGetterParams<CollectableRecovery>) =>
          p.data?.idDisplay ?? (p.data ? `#${p.data.id}` : ''),
        cellRenderer: RecoveryOfferCellRenderer,
        filter: 'agTextColumnFilter',
        flex: 1
      },
      {
        headerName: 'SPV name',
        colId: 'fundingSpv',
        valueGetter: (p: ValueGetterParams<CollectableRecovery>) => p.data?.fundingSpvLabel ?? '—',
        filter: 'agTextColumnFilter',
        flex: 1
      },
      {
        headerName: 'Recovery Date',
        colId: 'endDate',
        field: 'endDate',
        flex: 1
      },
      {
        headerName: 'Total',
        colId: 'totalAmount',
        field: 'totalAmount',
        type: 'rightAligned',
        flex: 1
      },
      {
        headerName: 'Collected',
        colId: 'collectedAmount',
        field: 'collectedAmount',
        type: 'rightAligned',
        flex: 1
      },
      {
        headerName: 'Remaining',
        colId: 'remainingAmount',
        field: 'remainingAmount',
        type: 'rightAligned',
        flex: 1
      },
      {
        headerName: 'Amount to collect',
        colId: 'amountToCollect',
        cellRenderer: AmountCellRenderer,
        sortable: false,
        filter: false,
        flex: 2,
        minWidth: 260
      }
    ],
    []
  )

  const totalToCollect = Money.sum(state.selectedRecoveries.map((r) => r.money))

  const toggleRow = (
    <div className="flex items-center justify-between gap-3">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <Switch checked={includeNotDueRecoveries} onCheckedChange={setIncludeNotDueRecoveries} />
        <span>Include recoveries that aren&apos;t due yet</span>
      </label>
      {!isCollectableLoading && (
        <span className="text-sm text-muted-foreground">
          {state.selectedRecoveries.length} of {collectableItems.length} selected
        </span>
      )}
    </div>
  )

  return (
    <div className="space-y-2">
      {toggleRow}
      {isCollectableLoading ? (
        <div className="flex items-center justify-center gap-2 h-[420px] text-sm text-muted-foreground border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading collectable recoveries…
        </div>
      ) : collectableRecoveries.length === 0 ? (
        <div className="flex items-center justify-center h-[420px] text-sm text-muted-foreground border rounded-md">
          {includeNotDueRecoveries
            ? 'No collectable advance recoveries for this trader.'
            : 'No advance recoveries due. Toggle the switch above to include recoveries that aren’t due yet.'}
        </div>
      ) : (
        <>
          <TableAgGridBulkSelection<CollectableRecovery>
            isBulkUpdating={true}
            rowData={collectableRecoveries}
            columnDefs={columnDefs}
            bulkItemSelected={isSelected}
            onBulkItemSelected={handleSelected}
            disableBulkSelectionItem={(r) => !r.remainingAmount.isPositive()}
            rowHeight={56}
            headerHeight={40}
            animateRows={true}
            enableCellTextSelection={false}
            pagination={false}
            paginationPageSize={20}
            theme={agGrid.theme}
            onGridReady={noop}
            quickFilterText=""
            defaultColDef={defaultColDef}
            columnTypes={agGrid.columnTypes}
            dataTypeDefinitions={agGrid.dataTypeDefinitions}
            className="w-full h-[420px]"
            getRowClass={getRowClass}
          />
          <div className="flex justify-between text-sm font-medium border-t pt-2">
            <span>Total to collect</span>
            <span>{totalToCollect.toString()}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default function SelectItemsStep({ ctx }: WizardStepComponentProps<CollectionStepCtx>) {
  const { state } = ctx
  const isFeeCollection = state.collectionType === COLLECTION_TYPE.FEES
  const isAdvanceRecovery = state.collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY

  const description = DESCRIPTION_BY_COLLECTION_TYPE[state.collectionType ?? COLLECTION_TYPE.FEES]

  return (
    <div className="space-y-4 w-full">
      <p className="text-sm text-muted-foreground">{description}</p>
      {isFeeCollection && <FeeItemsList />}
      {isAdvanceRecovery && <RecoveryItemsList />}
    </div>
  )
}
